import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { usePageState } from '@hooks/navigation/usePageState';
import { useCallRedirect } from '@hooks/useCallRedirect';
import { useContactRouter } from '@hooks/useContactRouter';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { AgentStatusEnum, CallDirectionEnum, ChannelEnum } from '@lib/enums';
import { POPUP_WINDOW_PREFIX } from '@lib/utils';
import { useJotaiAtom, useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom } from '@states/softphone/currentCallAtom';
import { softphoneAtom } from '@states/softphone/softphoneAtom';
type TAcwRedirectResult = {
  shouldClearAcw: boolean;
  shouldRedirectToWaiting: boolean;
};

/**
 * Determines whether stale ACW state should be cleared and whether the user
 * should be redirected to the waiting page.
 *
 * Gated on `isSoftphoneConnected` so that after re-authentication (when the
 * softphone hasn't reconnected yet) stale ACW state from localStorage is
 * cleaned up without triggering a spurious redirect to `/contacts/waiting`.
 */
export function evaluateAcwRedirect(params: {
  isActiveContact: boolean;
  isSoftphoneConnected: boolean;
  lastAcwContactId: Nullable<string>;
  lastRoutedContactId: Nullable<string>;
  isDealsLogPage: boolean;
}): TAcwRedirectResult {
  const hasCurrentContactBeenHandled = !params.isActiveContact && params.lastAcwContactId !== null && params.lastAcwContactId === params.lastRoutedContactId;
  return {
    shouldClearAcw: hasCurrentContactBeenHandled,
    shouldRedirectToWaiting: hasCurrentContactBeenHandled && !params.isDealsLogPage && params.isSoftphoneConnected
  };
}
export function useSoftphoneListener() {
  const $ = _c(36);
  const {
    agentStatus,
    isSimulatedCall,
    callDirection,
    channel
  } = useJotaiValue(softphoneAtom);
  const [t0, setCurrentCallState] = useJotaiAtom(currentCallAtom);
  const {
    lastRoutedContactId,
    lastRoutedContactMasterId,
    lastAcwContactId
  } = t0;
  const {
    isActiveContact,
    isSoftphoneConnected,
    contactId,
    contactMasterId
  } = useJotaiValue(contactSelector);
  const {
    goToWaitingPageForNextContact
  } = useContactRouter();
  const {
    performCallRedirect
  } = useCallRedirect();
  const {
    isDeferredCallRedirectEnabled
  } = useFeatureFlags();
  const {
    isWaitForCallPage,
    isDealsLogPage
  } = usePageState();
  const isOutboundCallRedirectDeferrable = isDeferredCallRedirectEnabled && callDirection === CallDirectionEnum.Outbound && channel === ChannelEnum.Unknown && !isWaitForCallPage;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = window.name?.startsWith(POPUP_WINDOW_PREFIX) ?? false;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isKittPopup = t1;
  let t2;
  let t3;
  if ($[1] !== agentStatus || $[2] !== contactId || $[3] !== setCurrentCallState) {
    t2 = () => {
      if (agentStatus === AgentStatusEnum.Acw) {
        setCurrentCallState(current => ({
          ...current,
          lastAcwContactId: contactId
        }));
      }
    };
    t3 = [contactId, agentStatus, setCurrentCallState];
    $[1] = agentStatus;
    $[2] = contactId;
    $[3] = setCurrentCallState;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== contactMasterId || $[7] !== goToWaitingPageForNextContact || $[8] !== isActiveContact || $[9] !== isDealsLogPage || $[10] !== isOutboundCallRedirectDeferrable || $[11] !== isSimulatedCall || $[12] !== isSoftphoneConnected || $[13] !== lastAcwContactId || $[14] !== lastRoutedContactId || $[15] !== lastRoutedContactMasterId || $[16] !== performCallRedirect || $[17] !== setCurrentCallState) {
    t4 = () => {
      const {
        shouldClearAcw,
        shouldRedirectToWaiting
      } = evaluateAcwRedirect({
        isActiveContact,
        isSoftphoneConnected,
        lastAcwContactId,
        lastRoutedContactId,
        isDealsLogPage
      });
      const hasCurrentContactBeenRouted = lastRoutedContactMasterId != null && lastRoutedContactMasterId === contactMasterId;
      if (shouldClearAcw) {
        setCurrentCallState(_temp);
        if (shouldRedirectToWaiting) {
          goToWaitingPageForNextContact();
        }
        return;
      }
      if (!isActiveContact || !isSoftphoneConnected || hasCurrentContactBeenRouted || isSimulatedCall === true || isOutboundCallRedirectDeferrable || isKittPopup || isDealsLogPage) {
        return;
      }
      performCallRedirect();
    };
    $[6] = contactMasterId;
    $[7] = goToWaitingPageForNextContact;
    $[8] = isActiveContact;
    $[9] = isDealsLogPage;
    $[10] = isOutboundCallRedirectDeferrable;
    $[11] = isSimulatedCall;
    $[12] = isSoftphoneConnected;
    $[13] = lastAcwContactId;
    $[14] = lastRoutedContactId;
    $[15] = lastRoutedContactMasterId;
    $[16] = performCallRedirect;
    $[17] = setCurrentCallState;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== callDirection || $[20] !== contactId || $[21] !== contactMasterId || $[22] !== goToWaitingPageForNextContact || $[23] !== isActiveContact || $[24] !== isDealsLogPage || $[25] !== isDeferredCallRedirectEnabled || $[26] !== isOutboundCallRedirectDeferrable || $[27] !== isSimulatedCall || $[28] !== isSoftphoneConnected || $[29] !== isWaitForCallPage || $[30] !== lastAcwContactId || $[31] !== lastRoutedContactId || $[32] !== lastRoutedContactMasterId || $[33] !== performCallRedirect || $[34] !== setCurrentCallState) {
    t5 = [contactId, contactMasterId, goToWaitingPageForNextContact, lastAcwContactId, lastRoutedContactId, lastRoutedContactMasterId, isActiveContact, isSoftphoneConnected, performCallRedirect, setCurrentCallState, isSimulatedCall, isDeferredCallRedirectEnabled, isWaitForCallPage, callDirection, isOutboundCallRedirectDeferrable, isKittPopup, isDealsLogPage];
    $[19] = callDirection;
    $[20] = contactId;
    $[21] = contactMasterId;
    $[22] = goToWaitingPageForNextContact;
    $[23] = isActiveContact;
    $[24] = isDealsLogPage;
    $[25] = isDeferredCallRedirectEnabled;
    $[26] = isOutboundCallRedirectDeferrable;
    $[27] = isSimulatedCall;
    $[28] = isSoftphoneConnected;
    $[29] = isWaitForCallPage;
    $[30] = lastAcwContactId;
    $[31] = lastRoutedContactId;
    $[32] = lastRoutedContactMasterId;
    $[33] = performCallRedirect;
    $[34] = setCurrentCallState;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  useEffect(t4, t5);
}
function _temp(current_0) {
  return {
    ...current_0,
    lastAcwContactId: null
  };
}